import React, { useEffect, useState } from 'react';
import { getContent } from '@/lib/api';
import { Seo } from '@/components/Seo';
import { PageHero } from '@/components/PageHero';
import { SectionHeading } from '@/components/SectionHeading';
import { Reveal, RevealGroup, RevealItem } from '@/components/Reveal';
import { Vision } from '@/components/home/Vision';
import { CtaSection } from '@/components/CtaSection';
import { motion } from 'framer-motion';
import { EASE } from '@/lib/motion';

export default function About() {
  const [about, setAbout] = useState(null);
  const [leadership, setLeadership] = useState([]);

  useEffect(() => {
    getContent('about').then(setAbout).catch(() => {});
    getContent('leadership').then(setLeadership).catch(() => {});
  }, []);

  const founder = about?.founderSpotlight;

  return (
    <>
      <Seo title='About Us' description='Legacy built on handwork, experience and execution — Sri Lakshmi Enterprises since 1970.' path='/about' image='/media/img/quarry-dusk.jpg' />
      <PageHero
        eyebrow='About Us'
        title={about?.headline || 'Legacy built on handwork, experience and execution.'}
        subtitle={about?.intro}
        image='/media/img/quarry-dusk.jpg'
      />

      {/* Logo + legacy statement (transparent full-colour logo, no white box) */}
      <section className='bg-[#F6F5F1] py-20 lg:py-28'>
        <div className='container-x grid items-center gap-12 lg:grid-cols-12'>
          <div className='lg:col-span-5'>
            <Reveal>
              <div className='flex items-center justify-center p-2'>
                <img src='/media/sle-logo-full.png' alt='Sri Lakshmi Enterprises — Shaping Ground. Building Tomorrow.' className='w-full max-w-md' />
              </div>
            </Reveal>
          </div>
          <div className='lg:col-span-7'>
            <SectionHeading eyebrow='Over Five Decades' title='A dynamic team of earth movers & contractors.' />
            <Reveal delay={0.1}>
              <p className='mt-6 text-lg leading-relaxed text-[#20272A]/70'>
                From a labour contract business into a full earthmoving operation — we believe in the power of handwork and experience, shaping the landscape and building the foundation of progress.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className='relative bg-[#20272A] py-20 lg:py-28'>
        <div className='grain absolute inset-0' />
        <div className='relative container-x'>
          <SectionHeading eyebrow='Our Journey' title='The path that built the brand.' light />
          <div className='mt-16 relative'>
            <div className='absolute left-0 top-0 hidden h-px w-full bg-white/10 md:block' style={{ top: '46px' }} />
            <div className='grid gap-12 md:grid-cols-3'>
              {(about?.timeline || []).map((t, i) => (
                <motion.div
                  key={t.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, ease: EASE, delay: i * 0.15 }}
                >
                  <div className='font-mono-num text-4xl font-medium text-[#B88A3B]'>{t.year}</div>
                  <div className='relative mt-4 mb-6 h-3 w-3 rounded-full bg-[#B88A3B]'>
                    <span className='absolute inset-0 motion-safe:animate-ping rounded-full bg-[#B88A3B]/50' />
                  </div>
                  <h3 className='font-head text-xl font-semibold text-[#F6F5F1]'>{t.title}</h3>
                  <p className='mt-3 text-sm leading-relaxed text-[#F6F5F1]/65'>{t.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Senior Founder spotlight */}
      {founder && (
        <section className='bg-[#F6F5F1] py-20 lg:py-28' data-testid='founder-spotlight'>
          <div className='container-x grid items-center gap-12 lg:grid-cols-12 lg:gap-16'>
            <div className='lg:col-span-6'>
              <span className='eyebrow text-[#B88A3B]'>{founder.eyebrow}</span>
              <h2 className='mt-3 font-head text-4xl font-semibold text-[#20272A] sm:text-5xl'>{founder.name}</h2>
              <p className='mt-2 text-sm uppercase tracking-wider text-[#075B61]'>{founder.title}</p>
              <p className='mt-7 text-lg leading-relaxed text-[#20272A]/75'>{founder.story}</p>
              {founder.quote && (
                <blockquote className='mt-7 border-l-2 border-[#B88A3B] pl-5 font-head text-xl italic text-[#20272A]/80'>
                  “{founder.quote}”
                </blockquote>
              )}
            </div>
            <div className='lg:col-span-6'>
              <Reveal>
                <figure className='relative overflow-hidden rounded-2xl border border-[#20272A]/10 shadow-[0_24px_70px_rgba(32,39,42,0.18)]'>
                  <img
                    src={founder.portrait}
                    alt={founder.name}
                    className='h-full w-full object-cover'
                    style={{ filter: 'sepia(0.18) saturate(0.95) contrast(1.03)' }}
                  />
                  <div className='pointer-events-none absolute inset-0' style={{ background: 'linear-gradient(160deg, rgba(184,138,59,0.10) 0%, transparent 35%, rgba(32,39,42,0.28) 100%)' }} />
                  <div className='grain pointer-events-none absolute inset-0 opacity-30' />
                </figure>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* Leadership — The people behind the endeavour */}
      <section className='bg-[#eef0ee] py-20 lg:py-28'>
        <div className='container-x'>
          <SectionHeading eyebrow='Leadership' title='The people behind the endeavour.' />
          <RevealGroup className='mt-14 grid gap-6 md:grid-cols-3'>
            {leadership.map((p) => (
              <RevealItem key={p.name}>
                <div className='group h-full rounded-2xl border border-[#20272A]/10 bg-white p-8 text-center transition-shadow duration-300 hover:shadow-[0_18px_60px_rgba(32,39,42,0.10)]' data-testid='leadership-card'>
                  <div className='mx-auto h-28 w-28 overflow-hidden rounded-full ring-2 ring-[#B88A3B]/30'>
                    {p.photo ? (
                      <img src={p.photo} alt={p.name} className='h-full w-full object-cover' />
                    ) : (
                      <div className='flex h-full w-full items-center justify-center bg-[#075B61] font-head text-2xl font-semibold text-[#F6F5F1]'>
                        {p.name.split(' ').map((n) => n[0]).join('').slice(-2)}
                      </div>
                    )}
                  </div>
                  <div className='mt-5 eyebrow text-[#B88A3B]'>{p.role}</div>
                  <h3 className='mt-2 font-head text-xl font-semibold text-[#20272A]'>{p.name}</h3>
                  <p className='mt-4 text-sm leading-relaxed text-[#20272A]/65'>{p.bio}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <Vision about={about} />
      <CtaSection />
    </>
  );
}
